import { z } from "zod"

export const AlfredPricingModuleCategorySchema = z.enum([
  "FEATURE",
  "USERS",
  "ONBOARDING",
  "MESSAGING",
])
export type AlfredPricingModuleCategory = z.infer<
  typeof AlfredPricingModuleCategorySchema
>

export interface AlfredPricingModule {
  id: string
  name: string
  description: string | null
  /** Serialized Lexical editor state, written in the admin app */
  content: string | null
  category: AlfredPricingModuleCategory
  monthlyPriceSek: number | null
  oneTimePriceSek: number | null
  unitPriceSek: number | null
  unitLabel: string | null
  supersededById: string | null
  sortOrder: number
}

export interface AlfredPricingPackage {
  id: string
  name: string
  tagline: string
  description: string
  isPopular: boolean
  sortOrder: number
  moduleIds: string[]
}

export interface AlfredQuote {
  /** The modules actually billed, after the rules in `calculateAlfredQuote` */
  moduleIds: string[]
  monthlySek: number
  oneTimeSek: number
}

export interface AlfredPricingCatalogPackage extends AlfredPricingPackage {
  quote: AlfredQuote
  /** The package before this one, for "Allt i Start, plus:" */
  previousPackageName: string | null
  /** Billed modules not billed in the previous package */
  addedModuleIds: string[]
}

export interface AlfredPricingCatalog {
  modules: AlfredPricingModule[]
  packages: AlfredPricingCatalogPackage[]
}

const nullablePriceSchema = z.number().int().min(0).max(1_000_000).nullable()

export const UpsertAlfredPricingModuleSchema = z.object({
  name: z.string().trim().min(1, "Namnet får inte vara tomt").max(100),
  description: z.string().trim().max(500).nullable(),
  content: z.string().max(500_000).nullable(),
  category: AlfredPricingModuleCategorySchema,
  monthlyPriceSek: nullablePriceSchema,
  oneTimePriceSek: nullablePriceSchema,
  unitPriceSek: nullablePriceSchema,
  unitLabel: z.string().trim().max(20).nullable(),
  supersededById: z.string().uuid().nullable(),
  sortOrder: z.number().int(),
})
export type UpsertAlfredPricingModule = z.infer<
  typeof UpsertAlfredPricingModuleSchema
>

export const UpsertAlfredPricingPackageSchema = z.object({
  name: z.string().trim().min(1, "Namnet får inte vara tomt").max(100),
  tagline: z.string().trim().max(100),
  description: z.string().trim().max(500),
  isPopular: z.boolean(),
  sortOrder: z.number().int(),
  moduleIds: z.array(z.string().uuid()).max(200),
})
export type UpsertAlfredPricingPackage = z.infer<
  typeof UpsertAlfredPricingPackageSchema
>
